import { Injectable } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { KreditorService } from '../shared/kreditor.service';
import { IUploadDocumentRestrictions } from '../shared/models';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  #fileUploadRestrictions: IUploadDocumentRestrictions;
  #invoiceUploadRestrictions: IUploadDocumentRestrictions;

  readonly #ttlMs = 5 * 60 * 1000;
  private settingsCache = new Map<string, CachedSetting>();

  constructor(private kreditorWs: KreditorService) { }

  getSettingValueByName(name: string): Observable<string> {
    const { cachedSettings, namesToFetch } = this.getSettingsFromCacheOrServer(name);

    if (namesToFetch.length === 0) {
      return of(cachedSettings[name]);
    }

    return this.kreditorWs
      .getServiceResult<string>(`settings/${encodeURIComponent(name)}`)
      .pipe(
        map(response => response.result),
        tap(value => {
          this.settingsCache.set(name, {
            value,
            expiresAt: Date.now() + this.#ttlMs
          });
        })
      );
  }

  private getSettingsFromCacheOrServer(settingsNames: string): {
    cachedSettings: Record<string, string>;
    namesToFetch: string[];
  } {
    const requestedNames = this.parseSettingsName(settingsNames);
    const cachedSettings: Record<string, string> = {};
    const namesToFetch: string[] = [];

    const now = Date.now();

    for (const name of requestedNames) {
      const cached = this.settingsCache.get(name);

      if (cached && cached.expiresAt > now) {
        cachedSettings[name] = cached.value;
      } else {
        namesToFetch.push(name);
      }
    }

    return {
      cachedSettings,
      namesToFetch
    };
  }

  getMultipleSettingsByName(settingsNames: string): Observable<Record<string, string>> {
    var { cachedSettings, namesToFetch } = this.getSettingsFromCacheOrServer(settingsNames);

    if (namesToFetch.length === 0) {
      return of(cachedSettings);
    }

    return this.kreditorWs
      .postServiceResult<Record<string, string>, any>(`settings/multiple`, { settingNames: namesToFetch.join(',') })
      .pipe(
        map(response => response.result),
        tap(apiSettings => {
          const expiresAt = Date.now() + this.#ttlMs;

          Object.entries(apiSettings).forEach(([key, value]) => {
            this.settingsCache.set(key, {
              value,
              expiresAt
            });
          });
        }),
        map(apiSettings => {
          return {
            ...cachedSettings,
            ...apiSettings
          };
        })
      );
  }

  private parseSettingsName(settingsNames: string): string[] {
    return settingsNames
      .split(/[,\s;]+/)
      .map(name => name.trim())
      .filter(Boolean);
  }

  getSettingAsBoolean(name: string): Observable<boolean> {
    return this.getSettingValueByName(name).pipe(map(_ => _.isTruthy()));
  }

  getFileUploadRestrictions(): Observable<IUploadDocumentRestrictions> {

    if (this.#fileUploadRestrictions != null) return of(this.#fileUploadRestrictions);

    return this.kreditorWs.getServiceResult<IUploadDocumentRestrictions>('settings/documentUploadRestrictions')
      .pipe(
        map(_ => _.result)
        , tap(restrictions => {
          this.#fileUploadRestrictions = restrictions
        })
      );
  }


  getInvoiceFileUploadRestrictions(): Observable<IUploadDocumentRestrictions> {

    if (this.#invoiceUploadRestrictions != null) return of(this.#invoiceUploadRestrictions);

    return this.kreditorWs.getServiceResult<IUploadDocumentRestrictions>('settings/caseInvoiceUploadRestrictions')
      .pipe(
        map(_ => _.result)
        , tap(restrictions => {
          this.#invoiceUploadRestrictions = restrictions
        })
      );
  }

  private publicSettings$ = this.kreditorWs
    .getServiceResult<Record<string, string>>('settings/publicUISettings')
    .pipe(
      map(data => data.result),
      catchError(() => of(null)),
      shareReplay(1)
    );

  getPublicUiSettings(): Observable<Record<string, string>> {
    return this.publicSettings$;
  }
}

interface CachedSetting {
  value: string;
  expiresAt: number;
}